import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Star, Trash2, Save, X, AlertCircle, CheckCircle, RefreshCw,
  DollarSign, BarChart3, Filter, Search, ToggleLeft, ToggleRight
} from 'lucide-react';
import MbelekoService from './services/mbelekoService';

const MbelekoAdminConfiguration = () => {
  // Tab Management
  const [activeTab, setActiveTab] = useState('base-amount');
  
  // Base Amount State
  const [baseAmountSettings, setBaseAmountSettings] = useState([]);
  const [currentBaseAmount, setCurrentBaseAmount] = useState(null);
  
  // Daraja State
  const [darajaLevels, setDarajaLevels] = useState([]);
  const [darajaStats, setDarajaStats] = useState(null);
  
  // Common State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Confirmation Dialog State
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null,
    type: 'warning' // 'warning', 'danger', 'info'
  });
  
  // Base Amount Form State
  const [baseAmountForm, setBaseAmountForm] = useState({
    name: '',
    base_amount: '',
    description: '',
    is_active: true
  });
  
  // Daraja Form State
  const [darajaForm, setDarajaForm] = useState({
    name: '',
    min_amount: '',
    max_amount: '',
    total_base_amount: '',
    enf_amount: '0',
    escrow_amount: '0',
    fundraise_amount: '0',
    undugu_amount: '0',
    jasiri_amount: '0',
    hero_amount: '0',
    royal_f_amount: '0',
    ma_amount: '',
    description: '',
    is_active: true
  });

  // Filter State
  const [filters, setFilters] = useState({
    is_active: '',
    search: '',
    type: ''
  });

  // Load data on component mount
  useEffect(() => {
    testApiConnection();
  }, []);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'base-amount') {
      loadBaseAmountData();
    } else if (activeTab === 'daraja') {
      loadDarajaData();
    }
  }, [activeTab]);

  const testApiConnection = async () => {
    const result = await MbelekoService.testConnection();
    if (result.success) {
      if (activeTab === 'base-amount') {
        loadBaseAmountData();
      } else if (activeTab === 'daraja') {
        loadDarajaData();
      }
    } else {
      setError(`API Connection Failed: ${result.error}`);
    }
  };

  // ==================== BASE AMOUNT FUNCTIONS ====================
  
  const loadBaseAmountData = async () => {
    setLoading(true);
    setError('');
    try {
      const [settingsData, currentData] = await Promise.all([
        MbelekoService.getBaseAmountSettings(),
        MbelekoService.getCurrentBaseAmount()
      ]);

      const settingsArray = Array.isArray(settingsData) ? settingsData : (settingsData.results || []);
      setBaseAmountSettings(settingsArray);
      setCurrentBaseAmount(currentData);
    } catch (err) {
      console.error('Load base amount data error:', err);
      setError(err.message || 'Failed to load base amount settings');
    } finally {
      setLoading(false);
    }
  };

  const handleBaseAmountSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const generatedName = baseAmountForm.name || `Base Amount ${formatCurrency(baseAmountForm.base_amount)}`;
      const formData = {
        name: generatedName,
        base_amount: baseAmountForm.base_amount,
        description: baseAmountForm.description || `System base amount of ${formatCurrency(baseAmountForm.base_amount)}`,
        is_active: baseAmountForm.is_active
      };

      if (modalType === 'create') {
        await MbelekoService.createBaseAmountSetting(formData);
        setSuccess('Base amount setting created successfully');
      } else if (modalType === 'edit') {
        await MbelekoService.updateBaseAmountSetting(selectedItem.id, formData);
        setSuccess('Base amount setting updated successfully');
      }

      closeModal();
      await loadBaseAmountData();
    } catch (err) {
      setError(MbelekoService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultBaseAmount = async (settingId) => {
    showConfirm({
      title: 'Set Default Base Amount',
      message: 'Are you sure you want to set this as the default base amount? This will affect all new groups.',
      confirmText: 'Set as Default',
      cancelText: 'Cancel',
      type: 'warning',
      onConfirm: async () => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
          const result = await MbelekoService.setDefaultBaseAmount(settingId);
          setSuccess(result.message || 'Default base amount updated successfully');
          await loadBaseAmountData();
        } catch (err) {
          setError(MbelekoService.getErrorMessage(err));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleToggleBaseAmountActive = async (setting) => {
    setLoading(true);
    setError('');

    try {
      await MbelekoService.updateBaseAmountSetting(setting.id, {
        is_active: !setting.is_active
      });
      setSuccess(`Setting ${!setting.is_active ? 'activated' : 'deactivated'} successfully`);
      await loadBaseAmountData();
    } catch (err) {
      setError(MbelekoService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ==================== DARAJA FUNCTIONS ====================
  
  const loadDarajaData = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('=== Loading Daraja Data ===');
      console.log('Filters:', filters);
      
      const [levelsData, statsData] = await Promise.all([
        MbelekoService.getDarajaLevels(filters),
        MbelekoService.getDarajaStats().catch(() => null)
      ]);

      console.log('Levels Data Response:', levelsData);
      console.log('Stats Data Response:', statsData);

      // Handle different response formats for levels
      let levelsArray = [];
      
      if (Array.isArray(levelsData)) {
        levelsArray = levelsData;
        console.log('✓ Using direct array');
      } else if (levelsData && Array.isArray(levelsData.results)) {
        levelsArray = levelsData.results;
        console.log('✓ Using results array');
      } else if (levelsData && Array.isArray(levelsData.darajas)) {
        levelsArray = levelsData.darajas;
        console.log('✓ Using darajas array');
      } else if (levelsData && Array.isArray(levelsData.daraja_levels)) {
        levelsArray = levelsData.daraja_levels;
        console.log('✓ Using daraja_levels array');
      } else if (levelsData && Array.isArray(levelsData.data)) {
        levelsArray = levelsData.data;
        console.log('✓ Using data array');
      } else if (levelsData) {
        console.warn('⚠ Unknown levels response format. Keys:', Object.keys(levelsData));
      }

      // CRITICAL FIX: Check if stats has the daraja_usage list
      if (statsData && Array.isArray(statsData.daraja_usage)) {
        console.log('✓ Found daraja_usage in stats! Using this instead.');
        levelsArray = statsData.daraja_usage;
      } else if (statsData && Array.isArray(statsData.darajas)) {
        console.log('✓ Found darajas in stats! Using this instead.');
        levelsArray = statsData.darajas;
      }

      console.log('Parsed levels count:', levelsArray.length);
      console.log('First level (if any):', levelsArray[0]);
      
      setDarajaLevels(levelsArray);

      // Set stats
      if (statsData) {
        console.log('✓ Using stats from API');
        setDarajaStats(statsData);
      } else {
        console.log('✓ Calculating stats from levels');
        const calculatedStats = {
          total_darajas: levelsArray.length,
          active_darajas: levelsArray.filter(d => d.is_active).length,
          basic_darajas: levelsArray.filter(d => d.is_basic || d.is_basic_daraja).length,
          advanced_darajas: levelsArray.filter(d => !d.is_basic && !d.is_basic_daraja).length
        };
        console.log('Calculated stats:', calculatedStats);
        setDarajaStats(calculatedStats);
      }

      console.log('=== Daraja Data Loaded Successfully ===');
    } catch (err) {
      console.error('❌ Load daraja data error:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      setError(err.message || 'Failed to load Daraja levels');
    } finally {
      setLoading(false);
    }
  };

  const handleDarajaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (modalType === 'create') {
        await MbelekoService.createDarajaLevel(darajaForm);
        setSuccess('Daraja level created successfully');
      } else if (modalType === 'edit') {
        await MbelekoService.updateDarajaLevel(selectedItem.id, darajaForm);
        setSuccess('Daraja level updated successfully');
      }

      closeModal();
      await loadDarajaData();
    } catch (err) {
      setError(MbelekoService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDarajaActive = async (daraja) => {
    setLoading(true);
    setError('');

    try {
      await MbelekoService.updateDarajaLevel(daraja.id, {
        is_active: !daraja.is_active
      });
      setSuccess(`Daraja ${!daraja.is_active ? 'activated' : 'deactivated'} successfully`);
      await loadDarajaData();
    } catch (err) {
      setError(MbelekoService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDaraja = async (darajaId) => {
    showConfirm({
      title: 'Delete Daraja Level',
      message: 'Are you sure you want to delete this Daraja level? This action cannot be undone and may affect groups using this level.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: async () => {
        setLoading(true);
        setError('');

        try {
          await MbelekoService.deleteDarajaLevel(darajaId);
          setSuccess('Daraja level deleted successfully');
          await loadDarajaData();
        } catch (err) {
          setError(MbelekoService.getErrorMessage(err));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // ==================== MODAL FUNCTIONS ====================
  
  const openModal = (type, item = null, category = null) => {
    setModalType(type);
    setSelectedItem(item);
    
    if (category === 'base-amount') {
      setBaseAmountForm(item ? {
        name: item.name || '',
        base_amount: item.base_amount || '',
        description: item.description || '',
        is_active: item.is_active !== false
      } : {
        name: '',
        base_amount: '',
        description: '',
        is_active: true
      });
    } else if (category === 'daraja') {
      setDarajaForm(item ? {
        name: item.name || '',
        min_amount: item.min_amount || '',
        max_amount: item.max_amount || '',
        total_base_amount: item.total_base_amount || '',
        enf_amount: item.enf_amount || item.enf_account_amount || '0',
        escrow_amount: item.escrow_amount || item.escrow_account_amount || '0',
        fundraise_amount: item.fundraise_amount || item.fundraise_account_amount || '0',
        undugu_amount: item.undugu_amount || '0',
        jasiri_amount: item.jasiri_amount || '0',
        hero_amount: item.hero_amount || '0',
        royal_f_amount: item.royal_f_amount || '0',
        ma_amount: item.ma_amount || '',
        description: item.description || '',
        is_active: item.is_active !== false
      } : {
        name: '',
        min_amount: '',
        max_amount: '',
        total_base_amount: '',
        enf_amount: '0',
        escrow_amount: '0',
        fundraise_amount: '0',
        undugu_amount: '0',
        jasiri_amount: '0',
        hero_amount: '0',
        royal_f_amount: '0',
        ma_amount: '',
        description: '',
        is_active: true
      });
    }
    
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setSelectedItem(null);
    setBaseAmountForm({
      name: '',
      base_amount: '',
      description: '',
      is_active: true
    });
    setDarajaForm({
      name: '',
      min_amount: '',
      max_amount: '',
      total_base_amount: '',
      enf_amount: '0',
      escrow_amount: '0',
      fundraise_amount: '0',
      undugu_amount: '0',
      jasiri_amount: '0',
      hero_amount: '0',
      royal_f_amount: '0',
      ma_amount: '',
      description: '',
      is_active: true
    });
  };

  // ==================== CONFIRMATION DIALOG ====================
  
  const showConfirm = (config) => {
    setConfirmConfig({
      title: config.title || 'Confirm Action',
      message: config.message || 'Are you sure you want to proceed?',
      confirmText: config.confirmText || 'Confirm',
      cancelText: config.cancelText || 'Cancel',
      onConfirm: config.onConfirm,
      type: config.type || 'warning'
    });
    setShowConfirmDialog(true);
  };

  const handleConfirmDialogConfirm = () => {
    if (confirmConfig.onConfirm) {
      confirmConfig.onConfirm();
    }
    setShowConfirmDialog(false);
  };

  const handleConfirmDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  // ==================== UTILITY FUNCTIONS ====================
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const applyFilters = () => {
    if (activeTab === 'base-amount') {
      loadBaseAmountData();
    } else if (activeTab === 'daraja') {
      loadDarajaData();
    }
  };

  const clearFilters = () => {
    setFilters({
      is_active: '',
      search: '',
      type: ''
    });
    setTimeout(() => {
      if (activeTab === 'base-amount') {
        loadBaseAmountData();
      } else if (activeTab === 'daraja') {
        loadDarajaData();
      }
    }, 100);
  };

  // ==================== TAB DEFINITIONS ====================
  
  const tabs = [
    { id: 'base-amount', label: 'Base Amount', icon: DollarSign, color: 'blue' },
    { id: 'daraja', label: 'Daraja Levels', icon: BarChart3, color: 'blue' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Mbeleko Admin Configuration</h1>
          <p className="text-gray-600">Manage system-wide settings and configurations</p>
        </div>

        {/* Tabs */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
          <div className="flex border-b border-gray-200">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              <span>{error}</span>
            </div>
            <button onClick={() => setError('')} className="text-red-700 hover:text-red-900">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              <span>{success}</span>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-700 hover:text-green-900">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Tab Content */}
        <div className="bg-white border border-gray-200 rounded-lg">
          {/* BASE AMOUNT TAB */}
          {activeTab === 'base-amount' && (
            <BaseAmountTab
              currentBaseAmount={currentBaseAmount}
              baseAmountSettings={baseAmountSettings}
              loading={loading}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              openModal={(type, setting) => openModal(type, setting, 'base-amount')}
              handleSetDefault={handleSetDefaultBaseAmount}
              handleToggleActive={handleToggleBaseAmountActive}
              loadData={loadBaseAmountData}
            />
          )}

          {/* DARAJA TAB */}
          {activeTab === 'daraja' && (
            <DarajaTab
              darajaLevels={darajaLevels}
              darajaStats={darajaStats}
              loading={loading}
              filters={filters}
              setFilters={setFilters}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              openModal={(type, daraja) => openModal(type, daraja, 'daraja')}
              handleToggleActive={handleToggleDarajaActive}
              handleDelete={handleDeleteDaraja}
              loadData={loadDarajaData}
              applyFilters={applyFilters}
              clearFilters={clearFilters}
            />
          )}
        </div>

        {/* Modals */}
        {showModal && activeTab === 'base-amount' && (
          <BaseAmountModal
            modalType={modalType}
            baseAmountForm={baseAmountForm}
            setBaseAmountForm={setBaseAmountForm}
            handleSubmit={handleBaseAmountSubmit}
            closeModal={closeModal}
            loading={loading}
            formatCurrency={formatCurrency}
          />
        )}

        {showModal && activeTab === 'daraja' && (
          <DarajaModal
            modalType={modalType}
            darajaForm={darajaForm}
            setDarajaForm={setDarajaForm}
            handleSubmit={handleDarajaSubmit}
            closeModal={closeModal}
            loading={loading}
            formatCurrency={formatCurrency}
          />
        )}

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <ConfirmDialog
            title={confirmConfig.title}
            message={confirmConfig.message}
            confirmText={confirmConfig.confirmText}
            cancelText={confirmConfig.cancelText}
            type={confirmConfig.type}
            onConfirm={handleConfirmDialogConfirm}
            onCancel={handleConfirmDialogCancel}
          />
        )}
      </div>
    </div>
  );
};

// ==================== BASE AMOUNT TAB COMPONENT ====================

const BaseAmountTab = ({ 
  currentBaseAmount, baseAmountSettings, loading, formatCurrency, formatDate,
  openModal, handleSetDefault, handleToggleActive, loadData 
}) => {
  return (
    <div className="p-6">
      {/* Current Base Amount Card */}
      {currentBaseAmount && (
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Active Base Amount</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Amount</p>
              <p className="text-3xl font-bold text-blue-600">TZS {formatCurrency(currentBaseAmount.current_base_amount)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Setting Name</p>
              <p className="text-lg font-medium text-gray-900">{currentBaseAmount.setting_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Last Updated</p>
              <p className="text-sm text-gray-900">{formatDate(currentBaseAmount.last_updated)}</p>
              {currentBaseAmount.updated_by && (
                <p className="text-xs text-gray-500">by {currentBaseAmount.updated_by}</p>
              )}
            </div>
          </div>
          {currentBaseAmount.description && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-1">Description</p>
              <p className="text-gray-900">{currentBaseAmount.description}</p>
            </div>
          )}
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Base Amount Settings</h2>
          <p className="text-sm text-gray-600 mt-1">
            {baseAmountSettings.length} setting{baseAmountSettings.length !== 1 ? 's' : ''} configured
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadData}
            disabled={loading}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => openModal('create')}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Create New Setting</span>
          </button>
        </div>
      </div>

      {/* Settings List */}
      {loading && baseAmountSettings.length === 0 ? (
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading base amount settings...</p>
        </div>
      ) : baseAmountSettings.length === 0 ? (
        <div className="p-12 text-center">
          <DollarSign className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No base amount settings configured</p>
          <button
            onClick={() => openModal('create')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Create First Setting
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Setting
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {baseAmountSettings.map((setting) => (
                <tr key={setting.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900 flex items-center">
                        {setting.name}
                        {setting.is_default && (
                          <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                            Default
                          </span>
                        )}
                      </div>
                      {setting.description && (
                        <div className="text-sm text-gray-500 mt-1">{setting.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900">
                      TZS {formatCurrency(setting.base_amount)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
                      setting.is_active 
                        ? 'bg-green-50 text-green-800 border-green-200' 
                        : 'bg-red-50 text-red-800 border-red-200'
                    }`}>
                      {setting.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {setting.created_by_name || 'System'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{formatDate(setting.updated_at)}</div>
                    {setting.updated_by_name && (
                      <div className="text-xs text-gray-500">by {setting.updated_by_name}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openModal('edit', setting)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Edit Setting"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {!setting.is_default && (
                        <button
                          onClick={() => handleSetDefault(setting.id)}
                          className="text-orange-500 hover:text-orange-700 p-1"
                          title="Set as Default"
                        >
                          <Star className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleActive(setting)}
                        className={`${
                          setting.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                        } p-1`}
                        title={setting.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {setting.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== DARAJA TAB COMPONENT ====================

const DarajaTab = ({ 
  darajaLevels, darajaStats, loading, filters, setFilters, formatCurrency, formatDate,
  openModal, handleToggleActive, handleDelete, loadData, applyFilters, clearFilters
}) => {
  return (
    <div className="p-6">
      {/* Stats Cards */}
      {darajaStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Total Darajas</p>
            <p className="text-2xl font-bold text-blue-600">{darajaStats.total_darajas || 0}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Active</p>
            <p className="text-2xl font-bold text-green-600">{darajaStats.active_darajas || 0}</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Basic Darajas</p>
            <p className="text-2xl font-bold text-purple-600">{darajaStats.basic_darajas || 0}</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Advanced Darajas</p>
            <p className="text-2xl font-bold text-orange-600">{darajaStats.advanced_darajas || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Search className="inline h-4 w-4 mr-1" />
              Search
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              placeholder="Search by name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filters.is_active}
              onChange={(e) => setFilters(prev => ({ ...prev, is_active: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="true">Active Only</option>
              <option value="false">Inactive Only</option>
            </select>
          </div>
          <div className="flex items-end space-x-2">
            <button
              onClick={applyFilters}
              disabled={loading}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              <Filter className="h-4 w-4" />
              <span>Filter</span>
            </button>
            <button
              onClick={clearFilters}
              disabled={loading}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Daraja Levels</h2>
          <p className="text-sm text-gray-600 mt-1">
            {darajaLevels.length} level{darajaLevels.length !== 1 ? 's' : ''} configured
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadData}
            disabled={loading}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => openModal('create')}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Create New Daraja</span>
          </button>
        </div>
      </div>

      {/* Daraja List */}
      {loading && darajaLevels.length === 0 ? (
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Daraja levels...</p>
        </div>
      ) : darajaLevels.length === 0 ? (
        <div className="p-12 text-center">
          <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No Daraja levels configured</p>
          <button
            onClick={() => openModal('create')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Create First Daraja
          </button>
          
          {/* Debug Info */}
          <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
            <p className="text-sm font-semibold text-yellow-800 mb-2">🔍 Debug Info:</p>
            <div className="text-xs text-yellow-700 space-y-1 font-mono">
              <p>• darajaLevels.length: {darajaLevels.length}</p>
              <p>• darajaStats: {darajaStats ? JSON.stringify(darajaStats) : 'null'}</p>
              <p>• Check browser console for API response details</p>
              <p className="mt-2 text-yellow-800 font-sans">
                <strong>Action:</strong> Open Browser Console (F12) to see detailed logs
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Daraja Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount Range
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Base Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mb-a Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Groups Using
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {darajaLevels.map((daraja) => (
                <tr key={daraja.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{daraja.name}</div>
                    {daraja.description && (
                      <div className="text-xs text-gray-500 mt-1">{daraja.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {daraja.min_amount && daraja.max_amount ? (
                      <>TZS {formatCurrency(daraja.min_amount)} - {formatCurrency(daraja.max_amount)}</>
                    ) : (
                      <span className="text-gray-400">Not set</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900">
                      TZS {formatCurrency(daraja.total_base_amount)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {daraja.ma_amount ? (
                      <>TZS {formatCurrency(daraja.ma_amount)}</>
                    ) : (
                      <span className="text-gray-400">Not set</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {daraja.total_groups || daraja.groups_count || 0} groups
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${
                      daraja.is_active 
                        ? 'bg-green-50 text-green-800 border-green-200' 
                        : 'bg-red-50 text-red-800 border-red-200'
                    }`}>
                      {daraja.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openModal('edit', daraja)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Edit Daraja"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(daraja)}
                        className={`${
                          daraja.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                        } p-1`}
                        title={daraja.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {daraja.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(daraja.id)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Delete Daraja"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== BASE AMOUNT MODAL COMPONENT ====================

const BaseAmountModal = ({ modalType, baseAmountForm, setBaseAmountForm, handleSubmit, closeModal, loading, formatCurrency }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {modalType === 'create' ? 'Create Base Amount Setting' : 'Edit Base Amount Setting'}
          </h3>
          <button onClick={closeModal} className="text-gray-400 hover:text-gray-600" disabled={loading}>
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Setting Name
            </label>
            <input
              type="text"
              value={baseAmountForm.name}
              onChange={(e) => setBaseAmountForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Standard Base Amount"
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate from amount</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Base Amount (TZS) *
            </label>
            <input
              type="number"
              value={baseAmountForm.base_amount}
              onChange={(e) => setBaseAmountForm(prev => ({ ...prev, base_amount: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter amount"
              required
              min="1000"
              max="10000000"
              step="1000"
            />
            <p className="text-xs text-gray-500 mt-1">
              Amount must be between TZS 1,000 and TZS 10,000,000
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={baseAmountForm.description}
              onChange={(e) => setBaseAmountForm(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter description (optional)"
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate</p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={baseAmountForm.is_active}
              onChange={(e) => setBaseAmountForm(prev => ({ ...prev, is_active: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Active
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={closeModal}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
              <Save className="h-4 w-4" />
              <span>{modalType === 'create' ? 'Create' : 'Update'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== DARAJA MODAL COMPONENT ====================

const DarajaModal = ({ modalType, darajaForm, setDarajaForm, handleSubmit, closeModal, loading }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full shadow-xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {modalType === 'create' ? 'Create Daraja Level' : 'Edit Daraja Level'}
          </h3>
          <button onClick={closeModal} className="text-gray-400 hover:text-gray-600" disabled={loading}>
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Daraja Name *
              </label>
              <input
                type="text"
                value={darajaForm.name}
                onChange={(e) => setDarajaForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., AWALI, MSINGI, PAA"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Amount (TZS)
              </label>
              <input
                type="number"
                value={darajaForm.min_amount}
                onChange={(e) => setDarajaForm(prev => ({ ...prev, min_amount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 50000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Amount (TZS)
              </label>
              <input
                type="number"
                value={darajaForm.max_amount}
                onChange={(e) => setDarajaForm(prev => ({ ...prev, max_amount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 75000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Base Amount (TZS)
              </label>
              <input
                type="number"
                value={darajaForm.total_base_amount}
                onChange={(e) => setDarajaForm(prev => ({ ...prev, total_base_amount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 56000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
              Mb-a Amount (TZS)
              </label>
              <input
                type="number"
                value={darajaForm.ma_amount}
                onChange={(e) => setDarajaForm(prev => ({ ...prev, ma_amount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 7000"
              />
            </div>
          </div>

          {/* Account Allocations */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3">Account Allocations</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { key: 'enf_amount', label: 'ENF' },
                { key: 'escrow_amount', label: 'Escrow' },
                { key: 'fundraise_amount', label: 'Fundraise' },
                { key: 'undugu_amount', label: 'Undugu' },
                { key: 'jasiri_amount', label: 'Jasiri' },
                { key: 'hero_amount', label: 'Hero' },
                { key: 'royal_f_amount', label: 'Royal F' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label}
                  </label>
                  <input
                    type="number"
                    value={darajaForm[key]}
                    onChange={(e) => setDarajaForm(prev => ({ ...prev, [key]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={darajaForm.description}
              onChange={(e) => setDarajaForm(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter description (optional)"
            />
          </div>

          {/* Active Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={darajaForm.is_active}
              onChange={(e) => setDarajaForm(prev => ({ ...prev, is_active: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Active
            </label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={closeModal}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
              <Save className="h-4 w-4" />
              <span>{modalType === 'create' ? 'Create' : 'Update'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== CONFIRMATION DIALOG COMPONENT ====================

const ConfirmDialog = ({ title, message, confirmText, cancelText, type, onConfirm, onCancel }) => {
  const typeStyles = {
    warning: {
      icon: 'bg-yellow-100 text-yellow-600',
      button: 'bg-yellow-600 hover:bg-yellow-700',
      border: 'border-yellow-200'
    },
    danger: {
      icon: 'bg-red-100 text-red-600',
      button: 'bg-red-600 hover:bg-red-700',
      border: 'border-red-200'
    },
    info: {
      icon: 'bg-blue-100 text-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700',
      border: 'border-blue-200'
    }
  };

  const styles = typeStyles[type] || typeStyles.warning;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
        <div className="p-6">
          <div className="flex items-start">
            <div className={`flex-shrink-0 w-12 h-12 rounded-full ${styles.icon} flex items-center justify-center`}>
              <AlertCircle className="h-6 w-6" />
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {title}
              </h3>
              <p className="text-sm text-gray-600">
                {message}
              </p>
            </div>
          </div>
        </div>

        <div className={`border-t ${styles.border} px-6 py-4 flex justify-end space-x-3`}>
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg ${styles.button}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MbelekoAdminConfiguration;